import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import Modal from '../components/Modal.jsx';
import { Panel } from '../components/Panel.jsx';
import { Resource, StatusGlyph } from '../components/StateViews.jsx';
import { formatRelative, formatTimestamp } from '../lib/format.js';
import { generatePassword, passwordPolicySummary } from '../lib/password.js';

const EMPTY_USER = { username: '', password: '', role: 'user', mustChangePassword: true };

export default function Users() {
  const { user: currentUser } = useAuth();
  const loadUsers = useCallback((signal) => endpoints.users(signal), []);
  const loadPolicy = useCallback((signal) => endpoints.passwordPolicy(signal), []);
  const usersState = useResource(loadUsers, []);
  const policyState = useResource(loadPolicy, []);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const users = Array.isArray(usersState.data) ? usersState.data : [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((account) => {
      if (needle && !account.username.toLowerCase().includes(needle)) return false;
      if (roleFilter !== 'all' && account.role !== roleFilter) return false;
      if (statusFilter === 'enabled' && !account.enabled) return false;
      if (statusFilter === 'disabled' && account.enabled) return false;
      return true;
    });
  }, [query, roleFilter, statusFilter, users]);

  function finish(message) {
    setModal(null);
    setError(null);
    setNotice(message);
    usersState.refresh();
  }

  function fail(actionError) {
    setError(actionError.message || 'Could not update the account.');
  }

  return (
    <div className="page users-page">
      <PageHeader
        eyebrow="Administration"
        title="Studio users"
        lede="Users have full Studio access. Admins have the same access plus user administration."
        actions={
          <>
            <button type="button" className="btn btn--quiet" disabled={policyState.status !== 'ready'} onClick={() => setModal('policy')}>Password policy</button>
            <button type="button" className="btn btn--primary" disabled={policyState.status !== 'ready'} onClick={() => setModal('add')}>Add user</button>
          </>
        }
      />

      {error ? <p className="notice notice--error" role="alert"><StatusGlyph tone="critical" shape="diamond" />{error}</p> : null}
      {notice ? <p className="notice" role="status"><StatusGlyph tone="good" shape="disc" />{notice}</p> : null}

      <Resource
        state={usersState}
        loadingLabel="Loading Studio users…"
        loadingRows={4}
        errorTitle="Could not load Studio users"
        isEmpty={(data) => !Array.isArray(data) || data.length === 0}
        emptyTitle="No users found"
        emptyBody="Add the first Studio account."
      >
        {() => (
          <Panel
            title="People with Studio access"
            subtitle={`${filtered.length} of ${users.length} shown`}
            actions={
              <div className="user-filters">
                <input className="search__input" type="search" aria-label="Search users" placeholder="Search users…" value={query} onChange={(event) => setQuery(event.target.value)} />
                <select className="select__input" aria-label="Filter by role" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                  <option value="all">All roles</option>
                  <option value="user">Users</option>
                  <option value="admin">Admins</option>
                </select>
                <select className="select__input" aria-label="Filter by status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="all">All statuses</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            }
          >
            {filtered.length ? (
              <div className="tablewrap">
                <table className="table users-table">
                  <caption className="visually-hidden">Studio users, roles, sign-in activity, and account status</caption>
                  <thead><tr><th scope="col">User</th><th scope="col">Role</th><th scope="col">Status</th><th scope="col">Last sign-in</th><th scope="col">Sessions</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
                  <tbody>
                    {filtered.map((account) => {
                      const isCurrent = account.username === currentUser?.username;
                      return (
                        <tr key={account.username} className={account.enabled ? undefined : 'is-disabled-row'}>
                          <th scope="row" className="mono">{account.username}{isCurrent ? <span className="account-you">You</span> : null}</th>
                          <td><span className="account-role-label">{account.role === 'admin' ? 'Admin' : 'User'}</span></td>
                          <td>
                            <span className={`account-status ${account.enabled ? 'is-enabled' : 'is-disabled'}`}><i aria-hidden="true" />{account.enabled ? 'Enabled' : 'Disabled'}</span>
                            {account.mustChangePassword ? <span className="account-flag">Password change due</span> : null}
                          </td>
                          <td>{account.lastLoginAt ? <><span className="nowrap">{formatRelative(account.lastLoginAt)}</span><span className="table__sub mono">{formatTimestamp(account.lastLoginAt)}</span></> : <span className="is-muted">Never</span>}</td>
                          <td className="num">{account.activeSessions}</td>
                          <td className="users-table__action">
                            {isCurrent ? <Link className="btn btn--quiet" to="/account">Account</Link> : <button type="button" className="btn btn--quiet" onClick={() => setModal({ type: 'edit', account })}>Edit</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="state__body">No users match these filters.</p>}
          </Panel>
        )}
      </Resource>

      {modal === 'add' ? <AddUserModal policy={policyState.data} onClose={() => setModal(null)} onCreated={(account) => finish(`${account.username} was added.`)} onError={fail} /> : null}
      {modal === 'policy' ? <PolicyModal policy={policyState.data} onClose={() => setModal(null)} onSaved={() => { setModal(null); setNotice('Password policy updated.'); policyState.refresh(); usersState.refresh(); }} onError={fail} /> : null}
      {modal?.type === 'edit' ? <EditUserModal account={modal.account} policy={policyState.data} onClose={() => setModal(null)} onUpdated={(message) => finish(message)} onError={fail} /> : null}
    </div>
  );
}

function AddUserModal({ policy, onClose, onCreated, onError }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_USER, password: generatePassword(policy) }));
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const account = await endpoints.createUser(form);
      onCreated(account);
    } catch (error) {
      onError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} eyebrow="Studio access" title="Add user" footer={<><button type="button" className="btn btn--quiet" onClick={onClose}>Cancel</button><button type="submit" form="add-user-form" className="btn btn--primary" disabled={submitting || !form.username || !form.password}>{submitting ? 'Adding…' : 'Add user'}</button></>}>
      <form id="add-user-form" className="modal-form" onSubmit={submit}>
        <label className="field"><span className="field__label">Username</span><input className="field__input mono" autoFocus required minLength="3" maxLength="32" autoCapitalize="none" value={form.username} onChange={(event) => setForm((value) => ({ ...value, username: event.target.value.toLowerCase() }))} /></label>
        <label className="field"><span className="field__label">Role</span><select className="field__input" value={form.role} onChange={(event) => setForm((value) => ({ ...value, role: event.target.value }))}><option value="user">User</option><option value="admin">Admin</option></select><small className="field__hint">Admins can administer other users. That is the only role difference.</small></label>
        <PasswordInput label="Initial password" value={form.password} policy={policy} onChange={(password) => setForm((value) => ({ ...value, password }))} />
        <label className="check-row"><input type="checkbox" checked={form.mustChangePassword} onChange={(event) => setForm((value) => ({ ...value, mustChangePassword: event.target.checked }))} /><span><strong>Require a new password at first sign-in</strong><small>The initial password stops working after the user changes it.</small></span></label>
      </form>
    </Modal>
  );
}

function PolicyModal({ policy, onClose, onSaved, onError }) {
  const [form, setForm] = useState(() => policy || { minLength: 12, requireUppercase: false, requireLowercase: false, requireNumber: false, requireSymbol: false, maxAgeDays: 0 });
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await endpoints.updatePasswordPolicy(form);
      onSaved();
    } catch (error) {
      onError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} eyebrow="Security" title="Password policy" footer={<><button type="button" className="btn btn--quiet" onClick={onClose}>Cancel</button><button type="submit" form="policy-form" className="btn btn--primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save policy'}</button></>}>
      <form id="policy-form" className="modal-form" onSubmit={submit}>
        <label className="field"><span className="field__label">Minimum password length</span><input className="field__input" type="number" min="8" max="128" value={form.minLength} onChange={(event) => setForm((value) => ({ ...value, minLength: Number(event.target.value) }))} /></label>
        <fieldset className="policy-checks"><legend>Character requirements</legend>{[['requireUppercase', 'Uppercase letter'], ['requireLowercase', 'Lowercase letter'], ['requireNumber', 'Number'], ['requireSymbol', 'Symbol']].map(([field, label]) => <label className="check-row check-row--compact" key={field}><input type="checkbox" checked={form[field]} onChange={(event) => setForm((value) => ({ ...value, [field]: event.target.checked }))} /><span><strong>{label}</strong></span></label>)}</fieldset>
        <label className="field"><span className="field__label">Require password changes</span><select className="field__input" value={form.maxAgeDays} onChange={(event) => setForm((value) => ({ ...value, maxAgeDays: Number(event.target.value) }))}><option value="0">Never</option><option value="30">Every 30 days</option><option value="60">Every 60 days</option><option value="90">Every 90 days</option><option value="180">Every 180 days</option></select><small className="field__hint">Expired users must change their password before returning to Studio.</small></label>
      </form>
    </Modal>
  );
}

function EditUserModal({ account, policy, onClose, onUpdated, onError }) {
  const loadActivity = useCallback((signal) => endpoints.userActivity(account.username, signal), [account.username]);
  const activity = useResource(loadActivity, [account.username]);
  const [role, setRole] = useState(account.role);
  const [password, setPassword] = useState(() => generatePassword(policy));
  const [forceChange, setForceChange] = useState(true);
  const [pending, setPending] = useState(null);

  async function run(action, callback, message) {
    setPending(action);
    try {
      await callback();
      onUpdated(message);
    } catch (error) {
      onError(error);
    } finally {
      setPending(null);
    }
  }

  function toggleEnabled() {
    if (account.enabled && !window.confirm(`Disable ${account.username}? Their active sessions will end immediately.`)) return;
    run('status', () => endpoints.updateUser(account.username, { enabled: !account.enabled }), `${account.username} was ${account.enabled ? 'disabled' : 'enabled'}.`);
  }

  return (
    <Modal open onClose={onClose} eyebrow="Edit user" title={account.username} size="large" footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      <div className="edit-user-grid">
        <section className="modal-section"><h3>Access</h3><label className="field"><span className="field__label">Role</span><select className="field__input" value={role} onChange={(event) => setRole(event.target.value)}><option value="user">User</option><option value="admin">Admin</option></select></label><div className="modal-actions"><button type="button" className="btn" disabled={pending || role === account.role} onClick={() => run('role', () => endpoints.updateUser(account.username, { role }), `${account.username} is now ${role === 'admin' ? 'an Admin' : 'a User'}.`)}>{pending === 'role' ? 'Saving…' : 'Save role'}</button><button type="button" className={`btn ${account.enabled ? 'btn--danger-quiet' : 'btn--primary'}`} disabled={pending} onClick={toggleEnabled}>{pending === 'status' ? 'Saving…' : account.enabled ? 'Disable user' : 'Enable user'}</button></div></section>
        <section className="modal-section"><h3>Reset password</h3><PasswordInput label="Temporary password" value={password} policy={policy} onChange={setPassword} /><label className="check-row check-row--compact"><input type="checkbox" checked={forceChange} onChange={(event) => setForceChange(event.target.checked)} /><span><strong>Require change at next sign-in</strong></span></label><button type="button" className="btn" disabled={pending || !password} onClick={() => run('password', () => endpoints.resetUserPassword(account.username, password, forceChange), `${account.username}'s password was reset.`)}>{pending === 'password' ? 'Resetting…' : 'Reset password'}</button></section>
        <section className="modal-section"><h3>Sessions</h3><p>{account.activeSessions} active session{account.activeSessions === 1 ? '' : 's'}.</p><button type="button" className="btn btn--quiet" disabled={pending || account.activeSessions === 0} onClick={() => run('sessions', () => endpoints.revokeUserSessions(account.username), `${account.username} was signed out everywhere.`)}>{pending === 'sessions' ? 'Signing out…' : 'Sign out everywhere'}</button></section>
        <section className="modal-section modal-section--activity"><h3>Recent account activity</h3>{activity.status === 'loading' ? <p className="is-muted">Loading activity…</p> : activity.data?.length ? <ol className="account-activity">{activity.data.map((event, index) => <li key={`${event.createdAt}-${index}`}><span><strong>{eventLabel(event.action)}</strong><small>{event.detail ? `${event.detail} · by ${event.actor}` : `By ${event.actor}`}</small></span><time dateTime={event.createdAt}>{formatRelative(event.createdAt)}</time></li>)}</ol> : <p className="is-muted">No account changes recorded yet.</p>}</section>
      </div>
    </Modal>
  );
}

function PasswordInput({ label, value, policy, onChange }) {
  async function copy() {
    try { await navigator.clipboard.writeText(value); } catch { /* Manual copy remains available. */ }
  }
  return <div className="field"><span className="field__label">{label}</span><div className="password-compose"><input className="field__input mono" type="text" value={value} onChange={(event) => onChange(event.target.value)} /><button type="button" className="btn btn--quiet" onClick={() => onChange(generatePassword(policy))}>Generate</button><button type="button" className="btn btn--quiet" onClick={copy}>Copy</button></div><small className="field__hint">{passwordPolicySummary(policy)}</small></div>;
}

function eventLabel(action) {
  return ({ user_created: 'User added', role_changed: 'Role changed', user_enabled: 'User enabled', user_disabled: 'User disabled', password_reset: 'Password reset', sessions_revoked: 'Sessions revoked', password_changed: 'Password changed', other_sessions_revoked: 'Sessions revoked' })[action] || action.replaceAll('_', ' ');
}
